import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { readStyle } from "./indicatorUtils";

function middleOfRange(
  dataList: KLineData[],
  endIndex: number,
  period: number
): number | null {
  if (endIndex < period - 1) return null;
  let high = dataList[endIndex]!.high;
  let low = dataList[endIndex]!.low;
  for (let j = 1; j < period; j++) {
    const idx = endIndex - j;
    high = Math.max(high, dataList[idx]!.high);
    low = Math.min(low, dataList[idx]!.low);
  }
  return (high + low) / 2;
}

export const createIchimoku = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const tenkanPeriod = (values["Tenkan-sen"] as number) ?? 9;
  const kijunPeriod = (values["Kijun-sen"] as number) ?? 26;
  const senkouBPeriod = (values["Senkou Span B"] as number) ?? 52;

  const tenkanS = readStyle(styles, "tenkan", "#0496FF");
  const kijunS = readStyle(styles, "kijun", "#EF476F");
  const chikouS = readStyle(styles, "chikou", "#26A69A");
  const spanAS = readStyle(styles, "spanA", "#26A69A");
  const spanBS = readStyle(styles, "spanB", "#EF5350");

  return {
    name: "ichimoku",
    shortName: "ICH",
    calcParams: [tenkanPeriod, kijunPeriod, senkouBPeriod],
    series: "price" as const,
    figures: [
      { key: "tenkan", title: "Tenkan-sen: ", type: "line" },
      { key: "kijun", title: "Kijun-sen: ", type: "line" },
      { key: "chikou", title: "Chikou Span: ", type: "line" },
      { key: "spanA", title: "Senkou Span A: ", type: "line" },
      { key: "spanB", title: "Senkou Span B: ", type: "line" },
    ],
    styles: {
      lines: [
        klineLineStyle(tenkanS.color, 1, lineStyleToKLine(tenkanS.style)),
        klineLineStyle(kijunS.color, 1, lineStyleToKLine(kijunS.style)),
        klineLineStyle(chikouS.color, 1, lineStyleToKLine(chikouS.style)),
        klineLineStyle(spanAS.color, 1, lineStyleToKLine(spanAS.style)),
        klineLineStyle(spanBS.color, 1, lineStyleToKLine(spanBS.style)),
      ],
    },
    regenerateFigures: () => [
      { key: "tenkan", title: "Tenkan-sen: ", type: "line" },
      { key: "kijun", title: "Kijun-sen: ", type: "line" },
      { key: "chikou", title: "Chikou Span: ", type: "line" },
      { key: "spanA", title: "Senkou Span A: ", type: "line" },
      { key: "spanB", title: "Senkou Span B: ", type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [tP, kP, sB] = (indicator.calcParams ?? [
        tenkanPeriod,
        kijunPeriod,
        senkouBPeriod,
      ]) as [number, number, number];
      const n = dataList.length;
      const results: Array<{
        tenkan?: number;
        kijun?: number;
        chikou?: number;
        spanA?: number;
        spanB?: number;
      }> = new Array(n);
      for (let i = 0; i < n; i++) results[i] = {};

      for (let i = 0; i < n; i++) {
        const tenkan = middleOfRange(dataList, i, tP);
        const kijun = middleOfRange(dataList, i, kP);
        const spanB = middleOfRange(dataList, i, sB);

        if (tenkan !== null) results[i]!.tenkan = tenkan;
        if (kijun !== null) results[i]!.kijun = kijun;

        if (i >= kP) {
          results[i - kP]!.chikou = dataList[i]!.close;
        }

        if (tenkan !== null && kijun !== null && i + kP < n) {
          results[i + kP]!.spanA = (tenkan + kijun) / 2;
        }
        if (spanB !== null && i + kP < n) {
          results[i + kP]!.spanB = spanB;
        }
      }

      return results;
    },
  };
};
